import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '../utils/analytics';
import axios from 'axios';
import './ContactUs.css';

const API_BASE_URL = '/api';

function ContactUs() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [characterCount, setCharacterCount] = useState(0);

  // Check if user is logged in and pre-fill data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/doctor-auth/check`, {
          withCredentials: true
        });

        if (response.data.isAuthenticated && response.data.doctor) {
          const doctor = response.data.doctor;
          setFormData(prev => ({
            ...prev,
            name: doctor.name || '',
            email: doctor.email || '',
            phone: doctor.phone || ''
          }));
        }
      } catch (err) {
        // User not logged in, that's okay
        console.log('User not logged in');
      }
    };

    checkAuth();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Character limit for message
    if (name === 'message' && value.length > 1000) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'message') {
      setCharacterCount(value.length);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/contact/submit`,
        formData,
        { withCredentials: true }
      );

      if (response.data.success) {
        // Track contact form submission
        logEvent('Contact', 'contact_form_submitted', {
          has_subject: formData.subject.trim().length > 0,
          has_phone: formData.phone.trim().length > 0,
          message_length: formData.message.trim().length,
        });

        setSuccess(true);
        setSubmissionId(response.data.submissionId);

        // Clear form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setCharacterCount(0);

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-us-page">
      <div className="contact-bg-orb"></div>
      <div className="contact-bg-mesh"></div>

      <div className="contact-container">
        {/* Header Section */}
        <div className="contact-header-section">
          <div className="contact-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            Get in Touch
          </div>
          <h1 className="contact-main-heading">We're Here to Help</h1>
          <p className="contact-subheading">
            Have questions or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="success-message-box">
            <div className="success-icon">✓</div>
            <h3>Thank You!</h3>
            <p>Your message has been received successfully.</p>
            {submissionId && <p className="submission-id">Submission ID: #{submissionId}</p>}
            <p className="success-note">We'll get back to you soon via email.</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="error-message-box">
            <div className="error-icon">✕</div>
            <p>{error}</p>
          </div>
        )}

        <div className="contact-content-grid">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  minLength={2}
                  maxLength={255}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject (Optional)</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  minLength={5}
                  maxLength={500}
                  placeholder="Brief description of your inquiry"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Message <span className="required">*</span>
                  <span className="char-count">{characterCount}/1000</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  required
                  minLength={10}
                  maxLength={1000}
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button type="submit" className="contact-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M7 17L17 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M7 7H17V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="contact-info-wrapper">
            <div className="info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <h3>Email Us</h3>
              <p>support@docplix.com</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <h3>Quick Response</h3>
              <p>We typically respond within 24 hours</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
              </div>
              <h3>24/7 Support</h3>
              <p>Our AI assistant is always available</p>
            </div>

            <div className="faq-section">
              <h3>Frequently Asked Questions</h3>
              <div className="faq-item">
                <strong>How long does it take to get a response?</strong>
                <p>We aim to respond to all inquiries within 24 hours.</p>
              </div>
              <div className="faq-item">
                <strong>Can I track my submission?</strong>
                <p>Yes! You'll receive a submission ID via email that you can use to track your inquiry.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
